import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGetAgreementsQuery } from '@client/api';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  isDefinedNumber,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
} from '@client/toolkit';

import { CompanyRentalAgreementRow } from './company-rental-agreement-row.component';

export function CompanyRentalAgreementsCard() {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { id } = useParams<{ id: string }>();

  const pageSize = 10;
  const [page, setPage] = useState(0);

  const { data, isFetching } = useGetAgreementsQuery(
    {
      'companyId.Equal': id,
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
    },
    { skip: id === null },
  );

  const agreements = useMemo(() => data?.items ?? [], [data?.items]);
  const hasAgreements = Array.isArray(agreements) && agreements.length > 0;
  const totalCount = data?.totalCount ?? 0;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex-1" variant="dense">
          {tGlobal('legalPerson.rentalAgreements')}
          <Text
            as="span"
            className="inline-block align-top ml-1 font-light"
            loading={isFetching}
            isEmpty={!isDefinedNumber(totalCount)}
            emptyValue={0}
            SkeletonProps={{ className: 'min-w-[20px]' }}
          >
            ({totalCount})
          </Text>
        </CardTitle>
        <CardActions />
      </CardHeader>

      <CardContent variant="fill">
        <Table className="overflow-hidden rounded-md">
          {!isFetching && !hasAgreements && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>
                {tCommon('noEntityFound', { entity: tGlobal('entity.agreement', { context: 'plural' }) })}
              </EmptyPlaceholder>
            </caption>
          )}

          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{tType('RelationshipType.Tenant')}</TableHeadCell>
              <TableHeadCell>{tType('RelationshipType.Owner')}</TableHeadCell>
              <TableHeadCell>{tGlobal('agreement.name')}</TableHeadCell>
              <TableHeadCell>{tGlobal('agreement.durationStart')}</TableHeadCell>
              <TableHeadCell>{tGlobal('agreement.durationEnd')}</TableHeadCell>
              <TableHeadCell>{tGlobal('agreement.totalYearlyAmount')}</TableHeadCell>
              <TableHeadCell>{tGlobal('agreement.status')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isFetching && Array.from(Array(5).keys()).map((x) => <CompanyRentalAgreementRow key={x} isFetching />)}

            {!isFetching &&
              hasAgreements &&
              agreements.map((agreement) => (
                <CompanyRentalAgreementRow key={agreement.id} agreement={agreement} isFetching={isFetching} />
              ))}
          </TableBody>
        </Table>
        <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
      </CardContent>
    </Card>
  );
}
